<template>
  <div class="date-data" :id="operationId">
    <operation-value
      :key="operationId"
      :element="element"
      :operations="operations"
      :container="'#' + operationId + '.date-data'"
      :getLang="getLang"
    />
    <transition name="fade" mode="out-in">
      <div v-if="showDateValue" class="section-value">
        <span class="label">
          <i class="sherpa-icon icon-calendario"></i>
          <transition name="fade-slide-left" mode="out-in">
            <span v-if="showToday">{{ getLang('diffDays') }}</span>
            <span v-else-if="showWeekDays || showMonthDays">{{ getLang('daysLabel') }}</span>
            <span v-else-if="showMonths">{{ getLang('months') }}</span>
            <span v-else>{{ getLang('dateSelection') }}</span>
          </transition>
        </span>
        <transition name="fade-slide-left" mode="out-in">
          <!-- date range -->
          <date-picker v-if="showBetweenFields"
            v-model:value="dateRangeProxy"
            :key="'range-' + element.id"
            :lang="getDateLang"
            format="DD/MM/YYYY"
            :first-day-of-week="1"
            :confirm="false"
            :shortcuts="null"
            :editable="true"
            :input-name="element.id + 'interval'"
            type="date"
            :range="true"
            :show-week-number="false"
            @change="dateValueChanged"
            @open="onOpen"
            @close="onClose"
          />

          <!-- year day -->
          <date-picker v-else-if="showYearDay"
            v-model:value="dateValueProxy"
            :key="'yearday-' + element.id"
            format="DD/MM"
            :value-type="'date'"
            :first-day-of-week="1"
            :confirm="false"
            :shortcuts="null"
            :editable="true"
            :show-week-number="false"
            @change="dateOfYearValueChanged"
            @open="onOpen"
            @close="onClose"
          />

          <!-- today -->
          <input v-else-if="showToday"
            type="text"
            class="input-value"
            inputmode="numeric"
            :value="element.operation.todayDiffDays"
            :key="'today-' + element.id"
            @input="onTodayInput"
          />

          <!-- week days -->
          <dual-list v-else-if="showWeekDays"
            :key="'weekdays-' + element.id"
            :domain="element.operation.weekDays"
            :getLang="getLang"
            :selected="selected"
          />

          <!-- month days -->
          <dual-list v-else-if="showMonthDays"
            :key="'monthdays-' + element.id"
            :domain="element.operation.monthDays"
            :getLang="getLang"
            :selected="selected"
          />

          <!-- months -->
          <dual-list v-else-if="showMonths"
            :key="'months-' + element.id"
            :domain="element.operation.yearMonths"
            :getLang="getLang"
            :selected="selected"
          />

          <!-- single date -->
          <date-picker v-else
            v-model:value="dateValueProxy"
            :key="'single-' + element.id"
            :lang="getDateLang"
            format="DD/MM/YYYY"
            :first-day-of-week="1"
            :confirm="false"
            :shortcuts="null"
            :editable="true"
            :input-name="element.id"
            type="date"
            :show-week-number="false"
            @change="dateValueChanged"
            @open="onOpen"
            @close="onClose"
          />
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import DatePicker from 'vue-datepicker-next'
import languages from '@/language/languages'
import operationValue from './operationValue'
import dualList from './dualList'

export default {
  name: 'date-data',
  components: {
    DatePicker,
    operationValue,
    dualList
  },
  props: {
    element: Object,
    getLang: Function,
    selected: Boolean,
    sendConditionResume: Function
  },
  data () {
    return {
    }
  },
  computed: {
    operations () {
      return [
        { key: 'equal', name: this.getLang('opDateEqualName'), description: this.getLang('opDateEqualDesc') },
        { key: 'not-equal', name: this.getLang('opDateNotEqualName'), description: this.getLang('opDateNotEqualDesc') },
        { key: 'between', name: this.getLang('opDateBetweenName'), description: this.getLang('opDateBetweenDesc') },
        { key: 'not-between', name: this.getLang('opDateNotBetweenName'), description: this.getLang('opDateNotBetweenSomeDesc') },
        { key: 'bigger', name: this.getLang('opDateBiggerName'), description: this.getLang('opDateBiggerDesc') },
        { key: 'bigger-or-equal', name: this.getLang('opDateBiggerOrEqualName'), description: this.getLang('opDateBiggerOrEqualDesc') },
        { key: 'less', name: this.getLang('opDateLessName'), description: this.getLang('opDateLessDesc') },
        { key: 'less-or-equal', name: this.getLang('opDateLessOrEqualName'), description: this.getLang('opDateLessOrEqualDesc') },
        { key: 'year-day', name: this.getLang('opDateYearDayName'), description: this.getLang('opDateYearDayDesc') },
        { key: 'year-day-today', name: this.getLang('opDateYearDayTodayName'), description: this.getLang('opDateYearDayTodayDesc') },
        { key: 'today', name: this.getLang('opDateTodayName'), description: this.getLang('opDateTodayDesc') },
        { key: 'week-days', name: this.getLang('opDateWeekDaysName'), description: this.getLang('opDateWeekDaysDesc') },
        { key: 'month-days', name: this.getLang('opDateMonthDaysName'), description: this.getLang('opDateMonthDaysDesc') },
        { key: 'year-months', name: this.getLang('opDateYearMonthsName'), description: this.getLang('opDateYearMonthsDesc') },
        { key: 'informed', name: this.getLang('opDateInformedName'), description: this.getLang('opDateInformedDesc') },
        { key: 'not-informed', name: this.getLang('opDateNotInformedSomeName'), description: this.getLang('opDateNotInformedSomeDesc') }
      ]
    },
    showDateValue () {
      return !['informed', 'not-informed']
        .includes(this.element.operation.operationValue)
    },
    showYearDay () {
      return ['year-day']
        .includes(this.element.operation.operationValue)
    },
    showToday () {
      return ['year-day-today', 'today']
        .includes(this.element.operation.operationValue)
    },
    showWeekDays () {
      return ['week-days']
        .includes(this.element.operation.operationValue)
    },
    showMonthDays () {
      return ['month-days']
        .includes(this.element.operation.operationValue)
    },
    showMonths () {
      return ['year-months']
        .includes(this.element.operation.operationValue)
    },
    showBetweenFields () {
      return ['between', 'not-between']
        .includes(this.element.operation.operationValue)
    },
    getDateLang () {
      return languages[this.$ml.current].datepicker
    },
    // -------- computed proxy for single dateValue ----------
    dateValueProxy: {
      get () {
        const v = this.element.operation.dateValue
        // si viene string, convierto a Date; si viene null o Date, devuelvo tal cual
        return typeof v === 'string' ? new Date(v) : v
      },
      set (newDate) {
        // newDate es Date|null; guardo en el DTO como ISO o null
        this.element.operation.dateValue = newDate instanceof Date
          ? newDate.toISOString()
          : null
      }
    },
    // -------- computed proxy para dateIntervalValue ---------
    dateRangeProxy: {
      get () {
        const arr = this.element.operation.dateIntervalValue || []
        return [
          arr[0] && typeof arr[0] === 'string' ? new Date(arr[0]) : arr[0],
          arr[1] && typeof arr[1] === 'string' ? new Date(arr[1]) : arr[1]
        ]
      },
      set ([start, end]) {
        // convierto de Date|null a ISO|null y guardo de una vez
        this.element.operation.dateIntervalValue = [
          start instanceof Date ? start.toISOString() : null,
          end instanceof Date ? end.toISOString() : null
        ]
      }
    },
    operationId () {
      return 'operation_' + this.element.id
    }
  },
  methods: {
    dateValueChanged (date) {
    },
    dateOfYearValueChanged (date) {
    },
    onOpen () {
      this.$emit('openCalendar', true)
    },
    onClose () {
      this.$emit('openCalendar', false)
    },
    getValuesStr () {
      const formatDate = date => {
        if (!date) return ''
        const d = new Date(date)
        if (isNaN(d)) return ''
        return new Intl.DateTimeFormat('es-ES').format(d) // Formato: DD/MM/YYYY
      }

      const formatDayMonth = date => {
        if (!date) return ''
        const d = new Date(date)
        if (isNaN(d)) return ''
        // Solo día y mes, por ejemplo "04/06"
        return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit' }).format(d)
      }

      if (this.element.operation && this.showDateValue) {
        if (this.showBetweenFields) {
          const [start, end] = this.element.operation.dateIntervalValue || []
          const formattedStart = formatDate(start)
          const formattedEnd = formatDate(end)
          if (!formattedStart || !formattedEnd) return ''
          return `: ${formattedStart} - ${formattedEnd}`
        } else if (this.showYearDay) {
          const formattedDayMonth = formatDayMonth(this.element.operation.dateValue)
          return formattedDayMonth ? `: ${formattedDayMonth}` : ''
        } else if (this.showToday) {
          return this.element.operation.todayDiffDays != null
            ? `: ${(this.element.operation.todayDiffDays >= 0 ? '+' : '')}${this.element.operation.todayDiffDays} ${this.getLang('days')}`
            : ''
        } else if (this.element.operation && this.showWeekDays) {
          const selected = this.element.operation.weekDays.selected
          if (selected && selected.length) {
            // extraemos solo los nombres y los unimos con ', '
            const names = selected.map(item => item.name).join(', ')
            return `: ${names}`
          }
          return ''
        } else if (this.element.operation && this.showMonthDays) {
          const selected = this.element.operation.monthDays.selected
          if (selected && selected.length) {
            // extraemos solo los nombres y los unimos con ', '
            const names = selected.map(item => item.name).join(', ')
            return `: ${names}`
          }
          return ''
        } else if (this.element.operation && this.showMonths) {
          const selected = this.element.operation.yearMonths.selected
          if (selected && selected.length) {
            // extraemos solo los nombres y los unimos con ', '
            const names = selected.map(item => item.name).join(', ')
            return `: ${names}`
          }
          return ''
        } else {
          const formattedDate = formatDate(this.element.operation.dateValue)
          return formattedDate ? `: ${formattedDate}` : ''
        }
      }

      return ''
    },
    onTodayInput (e) {
      const value = e.target.value

      // Permite solo: vacío, +123, -123, 123, +, - (pero no parsea + o - solos)
      const valid = /^[-+]?\d*$/.test(value)

      if (valid) {
        // Si está vacío o solo un signo, no parseamos
        if (value === '' || value === '+' || value === '-') {
          this.element.operation.todayDiffDays = null
        } else {
          this.element.operation.todayDiffDays = parseInt(value, 10)
        }
      } else {
        // Restaurar valor anterior como texto
        e.target.value =
          this.element.operation.todayDiffDays != null
            ? String(this.element.operation.todayDiffDays)
            : ''
      }
    },
    getMissingDomain (fullRange, selected) {
      const selectedIds = (selected || []).map(item => item.id)
      return fullRange
        .filter(value => !selectedIds.includes(String(value)))
        .map(value => ({ id: String(value), name: String(value) }))
    },
    getMissingWeekdayDomain (fullRange, selected) {
      const selectedIds = (selected || []).map(item => item.id)
      return fullRange
        .filter(value => !selectedIds.includes(String(value)))
        .map(value => ({ id: String(value), name: this.getLang('weekDays')[value - 1] }))
    }
  },
  beforeMount () {
    this.$emit('dateLoaded', true)
  },
  watch: {
    element: {
      handler () {
        this.sendConditionResume(this)
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.date-data {
  .mx-datepicker {
    width: 300px;
  }
}
</style>
